/**
 * Keep user-chosen name colours readable on the dark UI. The hue is theirs; saturation and
 * lightness are clamped so one person's pick can't be harsh or unreadable next to everyone else's.
 */
const MIN_LIGHTNESS = 0.66;
const MAX_LIGHTNESS = 0.82;
const MAX_SATURATION = 0.6;

function parseColor(input: string): [number, number, number] | null {
  const s = input.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(s);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split("").map((c) => c + c).join("") : hex[1];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/.exec(s);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}

/** Returns a CSS colour string with readable lightness, or undefined if the input isn't a colour we can parse. */
export function readableNameColor(color?: string | null): string | undefined {
  if (!color) return undefined;
  const rgb = parseColor(String(color));
  if (!rgb) return undefined;
  const [h, s, l] = rgbToHsl(...rgb);
  const nextS = Math.min(s, MAX_SATURATION);
  const nextL = Math.min(MAX_LIGHTNESS, Math.max(MIN_LIGHTNESS, l));
  return `hsl(${Math.round(h)} ${Math.round(nextS * 100)}% ${Math.round(nextL * 100)}%)`;
}
